import { onBeforeUnmount, onMounted } from 'vue'

const RESIZE_TYPE = 'mw-iframe-resize'
const SCROLL_TYPE = 'mw-iframe-scroll'

// Coalesce flurries of resize events. The success-message transition fires
// several ResizeObserver entries in quick succession (form unmount, message
// mount, font/layout settle); without debounce the host iframe pings up and
// down through several intermediate heights before settling.
const RESIZE_DEBOUNCE_MS = 150

export type ScrollTarget = 'top' | 'bottom'

// Cross-origin: window.top access throws in some sandboxed setups, so wrap.
function isIframed(): boolean {
  try {
    return window.top !== window.self
  } catch {
    return true
  }
}

// Bridges iframe -> host page via postMessage:
//   - posts {type: 'mw-iframe-resize', height} when content height settles
//     (debounced) so the host can size the iframe to its content
//   - exposes requestScroll(target) that asks the host to scrollIntoView the
//     iframe, aligning its top or bottom with the viewport
//
// When not iframed (e.g. opened directly during dev) the resize messages are
// posted to self and ignored; requestScroll falls back to window.scrollTo so
// the standalone URL still gives a useful UX.
export function useIframeBridge() {
  let observer: ResizeObserver | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | undefined
  let lastHeight = 0

  // Measure the mounted content box (#app), NOT document.documentElement.
  // Once the host sizes the iframe element to our content (e.g. 3348px for a
  // long form), the iframe viewport is that tall, so document(Element).scrollHeight
  // is floored at that height and can never report a SMALLER value - the iframe
  // would grow but never shrink (the success message left a huge blank iframe).
  // #app is a plain content-driven block, so it shrinks with its content.
  function measureHeight(): number {
    const el = document.getElementById('app')
    if (el) return Math.ceil(el.getBoundingClientRect().height)
    return Math.ceil(document.documentElement.scrollHeight)
  }

  function postHeight() {
    debounceTimer = undefined
    const height = measureHeight()
    if (height === lastHeight) return
    lastHeight = height
    window.parent.postMessage({ type: RESIZE_TYPE, height }, '*')
  }

  function scheduleHeightPost() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(postHeight, RESIZE_DEBOUNCE_MS)
  }

  function requestScroll(target: ScrollTarget = 'top') {
    if (!isIframed()) {
      window.scrollTo({
        top: target === 'top' ? 0 : document.documentElement.scrollHeight,
        behavior: 'smooth',
      })
      return
    }
    window.parent.postMessage({ type: SCROLL_TYPE, target }, '*')
  }

  onMounted(() => {
    if (!isIframed()) return
    // Observe #app (content-driven, shrinks) rather than documentElement
    // (floored at the iframe viewport height, so its box never shrinks and the
    // observer would not fire on collapse to the success message).
    const target = document.getElementById('app') ?? document.documentElement
    observer = new ResizeObserver(scheduleHeightPost)
    observer.observe(target)
    scheduleHeightPost()
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    if (debounceTimer) clearTimeout(debounceTimer)
  })

  return { requestScroll }
}
